import React, { useState } from 'react';
import { Car, TrendingUp, Zap, Phone } from 'lucide-react';
import { Button } from './ui/button';

const SimpleGainCalculator = () => {
  const [step, setStep] = useState(1);
  const [vehicle, setVehicle] = useState({
    type: '',
    fuel: '',
    power: ''
  });
  const [result, setResult] = useState(null);

  const vehicleTypes = [
    { id: 'citadine', label: 'Citadine', icon: '🚗' },
    { id: 'berline', label: 'Berline', icon: '🚙' },
    { id: 'suv', label: 'SUV', icon: '🚐' },
    { id: 'sportive', label: 'Sportive', icon: '🏎️' }
  ];

  const fuelTypes = [
    { id: 'diesel', label: 'Diesel', gain: { power: 35, torque: 40 } },
    { id: 'essence', label: 'Essence', gain: { power: 25, torque: 30 } },
    { id: 'hybrid', label: 'Hybride', gain: { power: 20, torque: 25 } }
  ];

  const powerRanges = [
    { id: 'low', label: '< 100 CV', multiplier: 1.0 },
    { id: 'medium', label: '100-150 CV', multiplier: 1.2 },
    { id: 'high', label: '150-200 CV', multiplier: 1.4 },
    { id: 'very_high', label: '> 200 CV', multiplier: 1.6 }
  ];

  const calculateGain = () => {
    const fuelType = fuelTypes.find(f => f.id === vehicle.fuel);
    const powerRange = powerRanges.find(p => p.id === vehicle.power);
    
    if (fuelType && powerRange) {
      const powerGain = Math.round(fuelType.gain.power * powerRange.multiplier);
      const torqueGain = Math.round(fuelType.gain.torque * powerRange.multiplier);
      
      setResult({
        power: powerGain,
        torque: torqueGain
      });
      setStep(4);
    }
  };

  const reset = () => {
    setStep(1);
    setVehicle({ type: '', fuel: '', power: '' });
    setResult(null);
  };

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-slate-950 to-slate-900" id="calculateur">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-4 bg-red-600/10 rounded-full mb-6">
            <Zap className="h-12 w-12 text-red-600" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Calculateur de Gains
          </h2>
          <p className="text-gray-400 text-lg">
            Estimez vos gains de performance en 3 étapes
          </p>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border-2 border-slate-700 p-8">
          {/* Progress Bar */}
          <div className="flex items-center justify-center mb-8">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-all ${
                  step >= s ? 'bg-red-600 text-white' : 'bg-slate-700 text-gray-400'
                }`}>
                  {s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 mx-2 transition-all ${
                    step > s ? 'bg-red-600' : 'bg-slate-700'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Type de véhicule */}
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white text-center mb-6">
                Quel type de véhicule ?
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {vehicleTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => {
                      setVehicle({ ...vehicle, type: type.id });
                      setStep(2);
                    }}
                    className="group p-6 rounded-xl border-2 border-slate-700 bg-slate-900/50 hover:border-red-600 hover:bg-slate-700/50 transition-all transform hover:scale-105"
                  >
                    <div className="text-center">
                      <div className="text-5xl mb-3">{type.icon}</div>
                      <p className="text-white font-semibold group-hover:text-red-600 transition-colors">
                        {type.label}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Carburant */}
          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white text-center mb-6">
                Type de carburant ?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {fuelTypes.map((fuel) => (
                  <button
                    key={fuel.id}
                    onClick={() => {
                      setVehicle({ ...vehicle, fuel: fuel.id });
                      setStep(3);
                    }}
                    className="p-6 rounded-xl border-2 border-slate-700 bg-slate-900/50 hover:border-red-600 hover:bg-slate-700/50 transition-all transform hover:scale-105"
                  >
                    <p className="text-xl font-bold text-white text-center">
                      {fuel.label}
                    </p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(1)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ← Retour
              </button>
            </div>
          )}

          {/* Step 3: Puissance */}
          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white text-center mb-6">
                Puissance actuelle ?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {powerRanges.map((power) => (
                  <button
                    key={power.id}
                    onClick={() => {
                      setVehicle({ ...vehicle, power: power.id });
                      calculateGain();
                    }}
                    className="p-6 rounded-xl border-2 border-slate-700 bg-slate-900/50 hover:border-red-600 hover:bg-slate-700/50 transition-all transform hover:scale-105"
                  >
                    <p className="text-xl font-bold text-white text-center">
                      {power.label}
                    </p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(2)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ← Retour
              </button>
            </div>
          )}

          {/* Step 4: Résultat */}
          {step === 4 && result && (
            <div className="space-y-8">
              <h3 className="text-3xl font-bold text-white text-center mb-6">
                🎉 Vos gains estimés !
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="text-center p-8 bg-gradient-to-br from-red-900/30 to-red-700/30 rounded-xl border-2 border-red-700">
                  <TrendingUp className="h-12 w-12 text-red-600 mx-auto mb-4" />
                  <p className="text-6xl font-bold text-red-600 mb-2">
                    +{result.power}%
                  </p>
                  <p className="text-xl text-white font-semibold">
                    Gain de Puissance
                  </p>
                  <p className="text-gray-400 mt-2">
                    En chevaux (CV)
                  </p>
                </div>

                <div className="text-center p-8 bg-gradient-to-br from-orange-900/30 to-orange-700/30 rounded-xl border-2 border-orange-700">
                  <Zap className="h-12 w-12 text-orange-600 mx-auto mb-4" />
                  <p className="text-6xl font-bold text-orange-600 mb-2">
                    +{result.torque}%
                  </p>
                  <p className="text-xl text-white font-semibold">
                    Gain de Couple
                  </p>
                  <p className="text-gray-400 mt-2">
                    En Newton-mètre (Nm)
                  </p>
                </div>
              </div>

              <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700">
                <p className="text-green-400 font-semibold mb-3 text-center">
                  ✨ Ce que cela signifie pour vous :
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-300">
                  <p>✅ Accélérations plus franches</p>
                  <p>✅ Reprises améliorées</p>
                  <p>✅ Consommation optimisée</p>
                  <p>✅ Conduite plus agréable</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:0749995728"
                  className="flex-1 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 rounded-xl transition-all transform hover:scale-105"
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Devis Gratuit - 07 49 99 57 28
                </a>
                <button
                  onClick={reset}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-semibold px-8 py-4 rounded-xl transition-all"
                >
                  Nouveau calcul
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-gray-400 text-sm mt-6">
          💡 Ces valeurs sont des estimations. Les gains réels peuvent varier selon le véhicule.
        </p>
      </div>
    </section>
  );
};

export default SimpleGainCalculator;
